/**
 * URL scrubbing for client telemetry. Telemetry must never carry free text, barcode contents, email
 * addresses, or anything identifying a vehicle or customer, and the app's routes and API calls put exactly those in
 * URLs: search text and registration numbers in query strings, self-check-in access codes (`id`/`code`), SAS
 * signatures on blob links, and VINs in API paths such as `vehicles/national-information/vin/{vin}`. So every
 * URL-like value is cleaned before it leaves the device: the fragment is dropped, every query value is replaced
 * (parameter names are kept, they are what makes a dependency readable), VIN-shaped path segments are replaced,
 * and email addresses are replaced anywhere.
 *
 * Unlike a deny-list of sensitive parameter names, redacting every query value cannot miss a parameter added later.
 */

/** The replacement for anything scrubbed. */
export const REDACTED = 'REDACTED';

/** Email addresses anywhere in a value, with the @ literal or percent-encoded. */
const EMAIL_PATTERN = /[a-z0-9._+-]+(?:@|%40)[a-z0-9-]+(?:\.[a-z0-9-]+)+/gi;

/** A whole path segment shaped like a VIN: 17 characters from the VIN alphabet, which excludes I, O and Q. */
const VIN_SEGMENT_PATTERN = /^[A-HJ-NPR-Z0-9]{17}$/i;

/**
 * Replaces every email address in a value.
 *
 * @param value Any string.
 * @returns The value with each email address replaced by {@link REDACTED}.
 */
export function redactEmails(value: string): string {
    return value.replace(EMAIL_PATTERN, REDACTED);
}

/**
 * Scrubs one URL, absolute (including capacitor:// and https://localhost origins) or relative: drops the fragment,
 * replaces every query value, replaces VIN-shaped path segments, and replaces email addresses.
 *
 * @param url The URL to scrub.
 * @returns The scrubbed URL.
 */
export function scrubUrl(url: string): string {
    const hashIndex = url.indexOf('#');
    const withoutFragment = hashIndex === -1 ? url : url.substring(0, hashIndex);
    const queryIndex = withoutFragment.indexOf('?');
    const path = queryIndex === -1 ? withoutFragment : withoutFragment.substring(0, queryIndex);
    const scrubbedPath = redactVinSegments(path);
    if (queryIndex === -1) {
        return redactEmails(scrubbedPath);
    }

    const query = withoutFragment
        .substring(queryIndex + 1)
        .split('&')
        .map(redactQueryPair)
        .join('&');
    return redactEmails(`${scrubbedPath}?${query}`);
}

/**
 * Scrubs every URL-like token in a value such as a dependency name ("GET https://host/path?search=x") or an
 * exception message or stack that quotes a request URL. Tokens are separated by whitespace, which is kept as is; a
 * token is treated as a URL when it contains '/', '?' or '#'. Other tokens pass through unchanged apart from email
 * addresses.
 *
 * @param value The value to scrub; undefined and empty values come back unchanged.
 * @returns The scrubbed value.
 */
export function scrubUrlsIn(value: string | undefined): string | undefined {
    if (!value) {
        return value;
    }
    return value
        .split(/(\s+)/)
        .map((token) => (isUrlLike(token) ? scrubUrl(token) : redactEmails(token)))
        .join('');
}

/**
 * Scrubs every string value in a telemetry property bag in place, with {@link scrubUrlsIn}. The SDK fills these bags
 * itself as well as from the app: an uncaught error's properties carry the page URL (`url`, and `errorSrc` as
 * `window.onerror@<page URL>:line:col`), and a hash-routed app such as marlin-web keeps its route, query string
 * included, in that URL's fragment. Scrubbing every value, as with query values, cannot miss a property added later.
 *
 * @param bag The property bag; anything that is not an object is left alone.
 */
export function scrubStringValuesIn(bag: unknown): void {
    if (!bag || typeof bag !== 'object') {
        return;
    }
    const values = bag as Record<string, unknown>;
    for (const key of Object.keys(values)) {
        if (typeof values[key] === 'string') {
            values[key] = scrubUrlsIn(values[key] as string);
        }
    }
}

function isUrlLike(token: string): boolean {
    return token.indexOf('/') !== -1 || token.indexOf('?') !== -1 || token.indexOf('#') !== -1;
}

function redactVinSegments(path: string): string {
    if (path.indexOf('/') === -1) {
        return VIN_SEGMENT_PATTERN.test(path) ? REDACTED : path;
    }
    return path
        .split('/')
        .map((segment) => (VIN_SEGMENT_PATTERN.test(segment) ? REDACTED : segment))
        .join('/');
}

/**
 * A pair with a name keeps the name and loses the value. A bare pair (no '=') has no name to keep and may itself be
 * the value (`search?ABC123`), so it is replaced whole. Empty pairs from '&&' or a trailing '?' stay empty.
 */
function redactQueryPair(pair: string): string {
    if (pair === '') {
        return pair;
    }
    const equals = pair.indexOf('=');
    return equals === -1 ? REDACTED : `${pair.substring(0, equals)}=${REDACTED}`;
}
